/**
 * Canonical ProvenLeap entity facts (Story 30.4). Single place for the
 * Organization identity used across JSON-LD `sameAs`, the About page (30.15),
 * and any structured-data builder.
 *
 * The `sameAs` profile URLs are PLACEHOLDERS until Story 30.14 creates the
 * real profiles (Wikidata QID, Crunchbase, G2, LinkedIn). Placeholder entries
 * are FILTERED OUT of the emitted schema — a dangling/fake `sameAs` URL
 * actively lowers entity confidence, so "omit until real" beats "emit fake".
 */

/** Canonical brand description — VERBATIM with apps/web/public/llms.txt (entity consistency). */
export const ORG_DESCRIPTION =
  "ProvenLeap is an automated marketing analytics and white-label client-reporting platform for agencies, built around a premium, extremely user-friendly experience. It consolidates 30 integrations (Google Ads, Meta, GA4, LinkedIn, Shopify, and more) into branded reports and dashboards, and connects to AI assistants like Claude and ChatGPT through the Model Context Protocol (MCP), so agencies can query client performance conversationally.";

export const ORG_NAME = "ProvenLeap";
export const ORG_URL = "https://provenleap.com";
export const ORG_LOGO = "https://provenleap.com/logo.svg";
export const FOUNDER_NAME = "Calin Paraipan";

/** Sentinel — any sameAs value containing this is a placeholder and is dropped. */
export const PLACEHOLDER = "__PLACEHOLDER__";

/**
 * Entity cross-references. Replace each PLACEHOLDER with the real URL as the
 * Story 30.14 profiles are created (EXTERNAL-PROVISIONING §30.14). Real values
 * flow into the Organization `sameAs` automatically; placeholders are omitted.
 */
export const ORG_SAME_AS: string[] = [
  "https://www.linkedin.com/company/112549006", // 30.14 — LIVE 2026-07-06
  "https://www.wikidata.org/wiki/Q140447486", // 30.14 Tier-1 — LIVE 2026-07-06
  "https://www.crunchbase.com/organization/provenleap", // 30.14 Tier-1 — LIVE 2026-07-05
  `https://www.g2.com/products/${PLACEHOLDER}`, // 30.14 Tier-2
];

export const FOUNDER_SAME_AS: string[] = [
  `https://www.linkedin.com/in/${PLACEHOLDER}`, // 30.14 — Calin's LinkedIn
];

export const FOUNDER_KNOWS_ABOUT = [
  "marketing analytics",
  "data visualization",
  "agency reporting",
  "SaaS",
];

/** Drop placeholder URLs — only real cross-references reach the schema. */
export function realSameAs(list: string[]): string[] {
  return list.filter((url) => !url.includes(PLACEHOLDER));
}
